import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { openUserProfile, OPEN_USER_PROFILE } from 'features/users/openUserProfile';
import { useLocation, useNavigate } from "react-router-dom";
import { useBreadcrumb, useBreadcrumbActions } from "shared/context/BreadcrumbContext";
import ClarifyCard from "shared/components/chat/ClarifyCard";
import { ArrowUp, Mic, Square, X } from "lucide-react";
import { fetchBoards, fetchCards } from "features/analytics/studio/api/analyticsApi";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primaryHover } from "shared/theme/trodoColors";
import { useTeam } from "shared/context/teamContext";
import { useChatSession } from "features/dashboard/hooks/useChatSession";
import {
    MAX_DASHBOARD_CONTEXT_BOARDS,
    MAX_DASHBOARD_CONTEXT_CARDS,
    countBoardAttachments,
    countCardAttachments,
} from "features/dashboard/constants/dashboardContextLimits";
import ChatAtContextPopover from "features/dashboard/components/ChatAtContextPopover";
import ComposerModeControl from "features/dashboard/components/ComposerModeControl";
import { createPortal } from "react-dom";
import ChatHistoryDrawer from "features/dashboard/components/ChatHistoryDrawer";
import { SIDE_PANEL_SLOT_ID } from "features/dashboard/chatSessions";
import JarvisHero, { JarvisBackdrop } from "features/dashboard/components/JarvisHero";
import AnswerMarkdown from "shared/components/chat/AnswerMarkdown";
import AnswerRenderer from "shared/components/chat/AnswerRenderer";
import InvestigationTrail from "shared/components/chat/InvestigationTrail";
import ChatAnswerFeedback from "shared/components/chat/ChatAnswerFeedback";
import RunEndedNote from "shared/components/chat/RunEndedNote";
import { openBesideChat } from "shared/components/records/recordsSurface";
import DeepRunProgress from "shared/components/chat/DeepRunProgress";
import ChatEntityDrawer, { fullRouteFor } from "shared/components/chat/ChatEntityDrawer";
import axiosInstance from "shared/services/api/axiosInstance";
import AgentRunDetailPage from "features/agent-analytics/pages/AgentRunDetailPage";
import { readScreenContext, hasUsefulContext } from "features/dashboard/askPanel/screenContext";
import { LEGACY_STUDIO_UI } from "shared/config/featureFlags";




/** Matches sidebar "Create New" button (theme.primary / trodoColors) */
const ACCENT = theme.primaryLight;
const ACCENT_HOVER = primaryHover;
const BORDER = theme.border;
const CHAT_INPUT_BG = "var(--surface-base)";




/** Active @mention at caret: token is @ + non-whitespace query up to caret */
function getAtMentionState(text, caret) {
  if (caret == null || caret < 1) return null;
  const before = text.slice(0, caret);
  const atIdx = before.lastIndexOf("@");
  if (atIdx < 0) return null;
  if (atIdx > 0) {
    const ch = before[atIdx - 1];
    if (ch && /[^\s\n]/.test(ch)) return null;
  }
  const afterAt = before.slice(atIdx + 1);
  if (/\s/.test(afterAt)) return null;
  return { start: atIdx, end: caret, query: afterAt };
}

function dashboardTagsFromMessage(msg) {
  const m = msg?.metadata;
  if (!m || typeof m !== "object") return [];
  const a = m.dashboardAttachments ?? m.dashboard_attachments;
  return Array.isArray(a) ? a : [];
}

// Force-rebuild marker — webpack was caching a stale chunk referencing
// a removed getTimeGreeting; this comment busts the chunk hash.
/**
 * Ask.
 *
 * Renders in two shapes from one implementation. `variant="page"` is the full
 * page at /:team/chat. `variant="panel"` is the same conversation in a column
 * beside whatever you were looking at — narrower, no breadcrumb, and with its
 * session held in state rather than `?session=`, because the pages it opens
 * over rebuild their query string on every filter change and would drop it.
 *
 * One implementation rather than two on purpose: clarify cards, plan-gate
 * approvals, answer feedback, the entity drawer and the run overlay are all
 * real behaviour, and a second copy of them would be wrong within a week.
 * Only one instance is ever mounted — the panel does not open on the chat page
 * — so the document-level link delegation below still has a single owner.
 */
/** URL flag that swaps the app rail for the agent workspace on this page. */
const AGENT_MODE_PARAM = 'agent';

/**
 * The switch between the two shapes of this page, sitting beside the title.
 * A switch rather than a link because nothing is navigated to: the same
 * conversation stays open and only what surrounds it changes.
 */
function AgentModeToggle({ on, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      title={on ? 'Leave agent mode' : 'Agent mode: your agents and chats beside the conversation'}
      className={`inline-flex shrink-0 items-center gap-2 rounded-md px-2 py-1 text-[12px] font-medium transition-colors hover:bg-surface-hover ${
        on ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
      }`}
    >
      <span className="whitespace-nowrap">Agent mode</span>
      <span
        aria-hidden
        className="relative inline-block h-[14px] w-[24px] shrink-0 rounded-full transition-colors"
        style={{ backgroundColor: on ? 'rgb(var(--trodo-primary-rgb))' : 'var(--border-strong)' }}
      >
        <span
          className={`absolute top-[2px] h-[10px] w-[10px] rounded-full bg-white transition-all ${on ? 'left-[12px]' : 'left-[2px]'}`}
        />
      </span>
    </button>
  );
}

/** The working state for a run that is still being written on the server. */
const RESUMING_STREAM = {
  thinking: true,
  thinkingLabel: 'Still running',
  tools: [],
  toolResults: [],
  reasoning: [],
  steps: [],
  plan: null,
  resultKind: null,
  body: '',
  streaming: true,
};

export default function ChatPage({ variant = 'page', screenContext = null, resumeSessionId = null, onOpenFull = null, onSessionChange = null, draft = null }) {
  // The conversation's send, for events that carry on in it (read once mounted).
  const sendMessageRef = useRef(null);
  const isPanel = variant === 'panel';
  useBreadcrumb(isPanel ? null : [{ label: 'Chat' }]);
  const { selectedTeam, isLoading: teamLoading } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || (teamId != null ? String(teamId) : null);
  const location = useLocation();
  const navigate = useNavigate();
  // Entity drawer state — opened by trodo://entity/<kind>/<id> link clicks
  // inside assistant markdown answers.
  // The panel's history menu picks a conversation; the page uses ?session=.
  const resumedRef = useRef(null);
  const [entityDrawer, setEntityDrawer] = useState(null);
  const [runDetailOverlay, setRunDetailOverlay] = useState(null);
  useEffect(() => {
    // EVERY entity kind opens in place — no click in a chat answer navigates
    // away from the conversation (Akshit, 3 Sep: "everything should open there
    // as a pop-up... not taking the user to the other page").
    //
    // `user` used to be carved out and sent to its profile page. Found again
    // on 3 Sep by clicking the USER column of a runs table: the row-click
    // delegation hands the click to whichever entity link sits in that cell,
    // so clicking a perfectly ordinary row threw the conversation away. The
    // drawer has always known how to fetch and render a user, and its "open
    // full view" button still offers the profile page for anyone who wants it.
    const openEntity = (kind, id) => {
      // A user opens the app-wide user drawer, the same one every page uses.
      if (kind === "user" && id) {
        openUserProfile(id);
        return;
      }
      // Runs skip the summary popup and open the full run view directly.
      if (kind === "run" && id) {
        setRunDetailOverlay({ runId: id });
        return;
      }
      // Spans do the same. A span has no standalone view — it lives inside its
      // run's trace — so we need its run_id first, which the link does not
      // carry. One fetch, then straight into the same overlay with the span
      // selected.
      //
      // This used to fall through to the summary drawer, which made opening a
      // span a three-step trip: click the id, read a proxy popup, press "Open
      // full view" — and that button then navigated AWAY from the chat to the
      // traces page, losing the conversation. Runs never did this.
      if (kind === "span" && id && teamId) {
        axiosInstance
          .get(`/agent-spans/${encodeURIComponent(id)}`, { params: { team_id: teamId } })
          .then((r) => {
            const runId = r?.data?.span?.run_id;
            if (!runId) throw new Error("span has no run");
            setRunDetailOverlay({ runId, initialSpanId: id });
          })
          // Runless span, or the fetch failed: fall back to the summary drawer
          // rather than leaving the user with nothing.
          .catch(() => setEntityDrawer({ kind, id }));
        return;
      }
      setEntityDrawer({ kind, id });
    };
    const onOpen = (e) => {
      const d = e?.detail;
      if (!d || !d.kind || !d.id) return;
      openEntity(d.kind, d.id);
    };
    window.addEventListener("trodo:chat:openEntity", onOpen);
    // Something in an answer asks Lucid to carry on in this conversation
    // ("Ask Lucid to add it" under a chart): sent here, not in a new one.
    const onDraft = (e) => {
      const d = e?.detail;
      if (!d || typeof d.prompt !== "string" || !d.prompt.trim()) return;
      sendMessageRef.current?.(d.prompt.trim());
    };
    window.addEventListener("trodo:chat:send", onDraft);
    // The run overlay sits above page drawers (z-[220]); a user opened from
    // inside it (its User cell) would land underneath, so it steps aside.
    const onUserOpen = () => { setRunDetailOverlay(null); setEntityDrawer(null); };
    window.addEventListener(OPEN_USER_PROFILE, onUserOpen);
    // Delegate clicks on ANY <a href="trodo://entity/<kind>/<id>"> in the
    // chat area — markdown links and widget links alike. The browser cannot
    // navigate to a `trodo://` URI, so without this clicks would silently
    // dead-end.
    const onClickAnyTrodoLink = (e) => {
      const a = e.target?.closest?.('a[href^="trodo://entity/"]');
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const m = href.match(/^trodo:\/\/entity\/([a-z_]+)\/(.+)$/i);
      if (!m) return;
      e.preventDefault();
      e.stopPropagation();
      openEntity(m[1], decodeURIComponent(m[2]));
    };
    document.addEventListener("click", onClickAnyTrodoLink, true);
    return () => {
      window.removeEventListener("trodo:chat:openEntity", onOpen);
      window.removeEventListener("trodo:chat:send", onDraft);
      window.removeEventListener(OPEN_USER_PROFILE, onUserOpen);
      document.removeEventListener("click", onClickAnyTrodoLink, true);
    };
  }, [teamId]);

  const handleEntityOpenFullView = useCallback((kind, id, data) => {
    setEntityDrawer(null);
    if (kind === "run" && id) {
      setRunDetailOverlay({ runId: id });
      return;
    }
    if (kind === "span" && id) {
      const runId = data?.run_id || data?.runId;
      if (runId) {
        setRunDetailOverlay({ runId, initialSpanId: id });
        return;
      }
    }
    // Beside the conversation, not instead of it.
    const route = fullRouteFor(kind, id, teamSlug);
    if (route) openBesideChat(route);
  }, [teamSlug]);

  const homePendingFromState =
    typeof location.state?.pendingHomeMessage === "string"
      ? location.state.pendingHomeMessage.trim()
      : "";
  // A page's drafted message with send set goes out the way the home composer's does.
  const homePendingOption = homePendingFromState || (draft && draft.send && draft.prompt ? draft.prompt : null);

  const {
    sessions,
    messages,
    activeSessionId,
    sessionsLoading,
    messagesLoading,
    sending,
    awaitingReply,
    error,
    setError,
    messagesHasMore,
    loadOlderMessages,
    selectSession,
    loadMessagesForSession,
    newChat,
    sendMessage,
    homeBootstrapMessage,
    setSearchParams,
  } = useChatSession(teamId, teamLoading, {
    homePendingMessage: homePendingOption,
    syncUrl: !isPanel,
  });
  sendMessageRef.current = sendMessage;

  // ── Agent mode ────────────────────────────────────────────────────────────
  // Ask is a page in the app like any other: the app rail is beside it, so
  // everything else stays one click away. Agent mode swaps that rail for the
  // agent workspace — new chat, agents, the conversation list — for someone
  // who is here to work on agents rather than passing through. The flag lives
  // in the URL so the state is shared, linkable and survives a refresh; the
  // rail (shared/components/layout/Sidebar.js) reads the same parameter.
  const agentMode = !isPanel && new URLSearchParams(location.search).get(AGENT_MODE_PARAM) === '1';
  const toggleAgentMode = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get(AGENT_MODE_PARAM) === '1') next.delete(AGENT_MODE_PARAM);
        else next.set(AGENT_MODE_PARAM, '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  useBreadcrumbActions(
    isPanel ? null : <AgentModeToggle on={agentMode} onToggle={toggleAgentMode} />,
  );

  // ── Screen context ────────────────────────────────────────────────────────
  // Read at submit, not for display. The chip only says that Ask can see the
  // screen; enumerating the page, range and filters in the UI was explaining
  // back to someone what they are already looking at.
  const [contextDismissed, setContextDismissed] = useState(false);
  const screenContextRef = useRef(screenContext);
  screenContextRef.current = screenContext;
  const showContextChip = Boolean(screenContext && !contextDismissed);

  // The panel needs to know which conversation this is, so that "open in full"
  // lands on the same one rather than on a blank page.
  useEffect(() => { onSessionChange?.(activeSessionId || null); }, [activeSessionId, onSessionChange]);

  // The panel's history menu hands us a session to resume.
  useEffect(() => {
    if (!resumeSessionId || resumedRef.current === resumeSessionId) return;
    resumedRef.current = resumeSessionId;
    selectSession(resumeSessionId);
  }, [resumeSessionId, selectSession]);

  // Prevents the effect re-firing sendMessage when deps churn mid-flight:
  // sendMessage identity changes as sending/activeSessionId update, and
  // location.search changes after setSearchParams({ session: sid }).
  const bootstrapFiredRef = useRef(false);

  /** First message creates the session (sendMessage); then clear router state so refresh does not resend. */
  useEffect(() => {
    if (bootstrapFiredRef.current || !homeBootstrapMessage || !teamId || teamLoading || sessionsLoading) return;
    bootstrapFiredRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        await sendMessage(homeBootstrapMessage);
        if (cancelled) return;
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: {} }
        );
      } catch (e) {
        console.error("Chat bootstrap from home failed:", e);
        bootstrapFiredRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    homeBootstrapMessage,
    teamId,
    teamLoading,
    sessionsLoading,
    sendMessage,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Strip ?session= only when leaving the chat page (true unmount). Do NOT depend on
  // setSearchParams — if its identity changes while ChatPage stays mounted (e.g. after send /
  // router updates), the previous effect's cleanup would run and remove session from the URL,
  // which resets the thread and looks like "opening a new chat" while history still lists the old session.
  // The strip is deferred a tick and skipped if the page is mounted again by
  // then: React's development double-mount runs this cleanup once on a page
  // that is not going anywhere, and stripping there dropped ?session= the
  // moment a conversation was opened by link.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      setTimeout(() => {
        if (mountedRef.current) return;
        let path = "";
        try {
          path = typeof window !== "undefined" ? window.location.pathname || "" : "";
        } catch {
          path = "";
        }
        if (!path.includes("/chat")) return;
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.delete("session");
            return n;
          },
          { replace: true }
        );
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: cleanup on unmount only
  }, []);

  const [input, setInput] = useState(() => (draft && !draft.send && draft.prompt ? draft.prompt : ""));
  const [isListening, setIsListening] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The full-height slot beside the content column (see Dashboard). Resolved
  // after mount because the slot is rendered by an ancestor.
  const [sideSlot, setSideSlot] = useState(null);
  useEffect(() => { setSideSlot(document.getElementById(SIDE_PANEL_SLOT_ID)); }, []);
  const [composerSel, setComposerSel] = useState({ start: 0, end: 0 });
  // The Analyze control: extended thinking = deep research at an effort level.
  const [extendedThinking, setExtendedThinking] = useState(false);
  const [effort, setEffort] = useState("medium");
  const textareaRef = useRef(null);
  // A drafted message is for the person to finish: the cursor waits at its end.
  useEffect(() => {
    if (!draft || draft.send || !draft.prompt) return undefined;
    const t = setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      try { el.setSelectionRange(end, end); } catch (_) { /* not a text input */ }
    }, 50);
    return () => clearTimeout(t);
  }, [draft]);
  const recognitionRef = useRef(null);
  const composerShellRef = useRef(null);
  const atMentionRef = useRef(null);
  /** The overflow-y chat pane — never use scrollIntoView (it scrolls page ancestors). */
  const messagesScrollRef = useRef(null);

  const [atPickerOpen, setAtPickerOpen] = useState(false);
  /** Keyboard highlight for inline @ picker (must match row order in `atPickerFlatRows`) */
  const [atHighlightIndex, setAtHighlightIndex] = useState(0);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerBoards, setPickerBoards] = useState([]);
  const [pickerBoardsLoading, setPickerBoardsLoading] = useState(false);
  const [cardsByBoardId, setCardsByBoardId] = useState({});
  const cardsByBoardIdRef = useRef({});
  /** Dedupe concurrent loads; values are promises that resolve to card arrays */
  const cardsLoadPromisesRef = useRef(new Map());
  /** @type {{ key: string, kind: 'board'|'card', boardId: number, cardId?: number, label: string }[]} */
  const [attachments, setAttachments] = useState([]);

  cardsByBoardIdRef.current = cardsByBoardId;

  // The @-picker attaches studio boards and cards. The studio is hidden
  // (TRO-466), so an @ is just a character: no mention state, which also
  // keeps the picker's keyboard handling (Enter picks a row) out of the way
  // of a message that happens to contain one. Dashboards v2 reaches Ask
  // through the page it is on (screenContext) instead.
  const atMention = useMemo(
    () => (LEGACY_STUDIO_UI ? getAtMentionState(input, composerSel.start) : null),
    [input, composerSel.start]
  );
  atMentionRef.current = atMention;

  const showAtPanel = Boolean(LEGACY_STUDIO_UI && teamId && (atPickerOpen || atMention));
  const showInlineAtPanel = Boolean(teamId && atMention);
  // The + button that opened a button-anchored picker is hidden for now; the
  // inline @-mention panel remains the way to attach boards/cards.

  const chatContextStatus = teamLoading ? "loading" : teamId == null ? "no_team" : "ok";

  // Smart auto-scroll: only follow the bottom when the user is already near
  // it, OR a brand-new message was added. Scroll ONLY the chat pane via
  // scrollTop — scrollIntoView walks all ancestors and randomly yanks the
  // page (and isNearBottom was measuring the wrong non-scrolling wrapper:
  // messagesEnd -> ul -> max-w wrapper, skipping the overflow-y container).

  // The clarification the analyst is waiting on, if any.
  //
  // Only the LAST message counts: once the user says anything else the
  // question is stale, and the backend will refuse the answer anyway (the
  // pending row is superseded). Showing a card that cannot be submitted is
  // worse than showing none.
  const [dismissedClarifications, setDismissedClarifications] = useState({});
  const [clarifyError, setClarifyError] = useState(null);
  const openClarification = useMemo(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return null;
    const inv = last.investigateStream;
    const id = inv?.clarificationId;
    const questions = inv?.clarifyQuestions;
    if (!id || !Array.isArray(questions) || !questions.length) return null;
    if (dismissedClarifications[id]) return null;
    return { id, questions };
  }, [messages, dismissedClarifications]);

  // Submitting sends the answers to the OPEN run rather than asking anew.
  // The visible message is a readable summary of what was chosen, so the
  // thread still reads as a conversation.
  const submitClarification = useCallback(async (answers) => {
    if (!openClarification) return;
    setClarifyError(null);
    const { id, questions } = openClarification;
    const parts = [];
    for (const q of questions) {
      const v = answers[q.id];
      if (v == null || (Array.isArray(v) && !v.length) || v === "") continue;
      const labelOf = (val) => (q.options || []).find((o) => o.value === val)?.label || String(val);
      parts.push(Array.isArray(v) ? v.map(labelOf).join(", ") : (q.type === "text" ? String(v) : labelOf(v)));
    }
    const summary = parts.length ? parts.join(" · ") : "Skip: use your best judgement";
    setDismissedClarifications((d) => ({ ...d, [id]: true }));
    try {
      await sendMessage(summary, { clarify: { clarificationId: id, answers } });
    } catch (e) {
      // Put the card back: the question is still open server-side.
      setDismissedClarifications((d) => { const n = { ...d }; delete n[id]; return n; });
      setClarifyError("Could not send that answer: please try again.");
    }
  }, [openClarification, sendMessage]);
  // The question box takes room from the thread above it: keep the end of the
  // conversation — the question it asks about — in view rather than behind it.
  const clarifyOpenId = openClarification ? openClarification.id : null;
  useEffect(() => {
    if (!clarifyOpenId) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = messagesScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [clarifyOpenId]);

  const skipClarification = useCallback(() => {
    if (openClarification) submitClarification({});
  }, [openClarification, submitClarification]);
  // Closing the card hides it; the question stays in the thread and the run
  // waits. Closing used to answer "Skip" and set the research going.
  const dismissClarification = useCallback(() => {
    if (openClarification) setDismissedClarifications((d) => ({ ...d, [openClarification.id]: true }));
  }, [openClarification]);

  // The quick run this page is streaming, if any: what Stop cancels. Deep runs
  // stop from their progress card.
  const liveRunId = useMemo(() => {
    const last = messages[messages.length - 1];
    const inv = last?.investigateStream;
    return inv?.streaming && inv.askRunId && inv.runMode !== "deep" ? inv.askRunId : null;
  }, [messages]);
  const [stopping, setStopping] = useState(false);
  useEffect(() => { if (!liveRunId) setStopping(false); }, [liveRunId]);
  const stopRun = useCallback(async () => {
    if (!liveRunId || !teamId) return;
    setStopping(true);
    try {
      await axiosInstance.post(`/ask/runs/${encodeURIComponent(liveRunId)}/cancel`, { team_id: teamId }, { params: { team_id: teamId } });
    } catch (_) {
      setStopping(false);
    }
  }, [liveRunId, teamId]);
  const billingHref = selectedTeam?.name ? `/${encodeURIComponent(selectedTeam.name)}/settings/billing` : "/settings/billing";
  const prevMsgCountRef = useRef(messages.length);
  useEffect(() => {
    const scroller = messagesScrollRef.current;
    if (!scroller) return undefined;

    const slack = 140;
    const distanceFromBottom =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const isNearBottom = distanceFromBottom < slack;
    const newMessageAdded = messages.length > prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (!(newMessageAdded || isNearBottom)) return undefined;

    // Instant for stream chunk updates (smooth animations stack and fight).
    // Smooth only when a whole new message lands.
    if (newMessageAdded) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    } else {
      scroller.scrollTop = scroller.scrollHeight;
    }
    return undefined;
  }, [messages]);

  // Click-outside only applies to the @-context popover. The history
  // drawer has its own backdrop click and ESC handling — wiring it here
  // would race with the drawer's own dismissal.
  useEffect(() => {
    if (!showAtPanel) return undefined;
    const onDoc = (e) => {
      if (composerShellRef.current?.contains(e.target)) return;
      setAtPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showAtPanel]);

  const loadPickerBoards = useCallback(async () => {
    if (!teamId) return;
    setPickerBoardsLoading(true);
    try {
      const res = await fetchBoards({ teamId });
      const list = res?.data ?? res?.boards ?? [];
      setPickerBoards(Array.isArray(list) ? list : []);
    } catch {
      setPickerBoards([]);
    } finally {
      setPickerBoardsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (showAtPanel && teamId) {
      loadPickerBoards();
    }
  }, [showAtPanel, teamId, loadPickerBoards]);

  useEffect(() => {
    if (atPickerOpen) {
      setPickerSearch("");
    }
  }, [atPickerOpen]);

  const loadCardsForBoard = useCallback(async (rawBoardId) => {
    const boardId = parseInt(String(rawBoardId), 10);
    if (!teamId || Number.isNaN(boardId) || boardId < 1) return null;

    const snap = cardsByBoardIdRef.current[boardId];
    if (snap && Array.isArray(snap.cards) && !snap.error) return snap.cards;

    const pending = cardsLoadPromisesRef.current.get(boardId);
    if (pending) return pending;

    const promise = (async () => {
      setCardsByBoardId((prev) => ({
        ...prev,
        [boardId]: { loading: true, cards: null, error: false },
      }));

      try {
        const res = await fetchCards(boardId, teamId);
        const cards = res?.data ?? res?.cards ?? [];
        const arr = Array.isArray(cards) ? cards : [];
        setCardsByBoardId((prev) => ({
          ...prev,
          [boardId]: {
            loading: false,
            cards: arr,
            error: false,
          },
        }));
        return arr;
      } catch {
        setCardsByBoardId((prev) => ({
          ...prev,
          [boardId]: { loading: false, cards: [], error: true },
        }));
        return [];
      } finally {
        cardsLoadPromisesRef.current.delete(boardId);
      }
    })();

    cardsLoadPromisesRef.current.set(boardId, promise);
    return promise;
  }, [teamId]);

  /** When @ picker is open, load cards for every board so boards + cards can be searched together */
  useEffect(() => {
    if (!showAtPanel || !teamId || pickerBoardsLoading || pickerBoards.length === 0) return;
    pickerBoards.forEach((b) => {
      const id = parseInt(String(b.id), 10);
      if (!Number.isNaN(id) && id >= 1) void loadCardsForBoard(id);
    });
  }, [showAtPanel, teamId, pickerBoards, pickerBoardsLoading, loadCardsForBoard]);

  const stripActiveAtMention = useCallback(() => {
    const m = atMentionRef.current;
    if (!m) return;
    setInput((prev) => prev.slice(0, m.start) + prev.slice(m.end));
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(m.start, m.start);
      setComposerSel({ start: m.start, end: m.start });
    });
  }, []);

  const addBoardAttachment = useCallback((board) => {
    const id = Number(board.id);
    if (!id) return;
    const key = `b-${id}`;
    const m = atMentionRef.current;
    let didAdd = false;
    setAttachments((prev) => {
      if (prev.some((a) => a.key === key)) return prev;
      if (countBoardAttachments(prev) >= MAX_DASHBOARD_CONTEXT_BOARDS) return prev;
      didAdd = true;
      return [...prev, { key, kind: "board", boardId: id, label: `Board: ${board.name || "Untitled"}` }];
    });
    setAtPickerOpen(false);
    if (didAdd && m) {
      setInput((prev) => prev.slice(0, m.start) + prev.slice(m.end));
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(m.start, m.start);
        setComposerSel({ start: m.start, end: m.start });
      });
    }
  }, []);

  const addCardAttachment = useCallback((boardId, card) => {
    const bid = Number(boardId);
    const cid = Number(card.id);
    if (!bid || !cid) return;
    const key = `c-${bid}-${cid}`;
    const title = card.title || "Untitled card";
    const m = atMentionRef.current;
    let didAdd = false;
    setAttachments((prev) => {
      if (prev.some((a) => a.key === key)) return prev;
      if (countCardAttachments(prev) >= MAX_DASHBOARD_CONTEXT_CARDS) return prev;
      didAdd = true;
      return [...prev, { key, kind: "card", boardId: bid, cardId: cid, label: `Card: ${title}` }];
    });
    setAtPickerOpen(false);
    if (didAdd && m) {
      setInput((prev) => prev.slice(0, m.start) + prev.slice(m.end));
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(m.start, m.start);
        setComposerSel({ start: m.start, end: m.start });
      });
    }
  }, []);

  const removeAttachment = useCallback((key) => {
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  }, []);

  const canDictate =
    typeof window !== "undefined" &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = false;
    // Keep listening until the user clicks stop — a dictated question is
    // often several sentences, and single-utterance mode cut it at the first
    // pause. onresult appends each finalized utterance as it lands.
    rec.continuous = true;
    rec.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      const t = last[0].transcript.trim();
      if (!t) return;
      setInput((prev) => {
        const p = prev.trim();
        return p ? `${p} ${t}` : t;
      });
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
    } catch {
      setIsListening(false);
    }
  }, []);

  const toggleMic = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, stopListening, startListening]);

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // A DEEP RUN'S "done" ARRIVES BEFORE ITS ANSWER. The engine emits done, and
  // the research worker writes the answer into this thread a couple of
  // seconds later — measured 2.5s. Reloading once, on done, fetched the thread
  // before the answer existed and nothing asked again, so the progress card
  // folded over an empty space until the page was refreshed. Ask until the
  // run's answer is there, backing off; a run that failed has no answer and
  // the loop simply ends.
  const reloadUntilDeepAnswer = useCallback(async (runId) => {
    if (!activeSessionId) return;
    for (const wait of [0, 1500, 3000, 6000, 10000, 15000]) {
      // eslint-disable-next-line no-await-in-loop
      if (wait) await new Promise((r) => setTimeout(r, wait));
      // eslint-disable-next-line no-await-in-loop
      const list = await loadMessagesForSession(activeSessionId, null, { silent: true }).catch(() => null);
      if ((list || []).some((m) => m?.metadata?.ask_run_id === runId && m?.metadata?.ask_deep_report)) return;
    }
  }, [activeSessionId, loadMessagesForSession]);

  const sendPrompt = async () => {
    const t = input.trim();
    if ((!t && attachments.length === 0) || !teamId) return;
    const toSend = [...attachments];
    setInput("");
    setAttachments([]);
    setAtPickerOpen(false);
    textareaRef.current?.focus();
    await sendMessage(t, {
      attachments: toSend,
      // Extended thinking = deep research at the chosen effort. Off = Analyze,
      // and the server picks quick on its own — no mode param needed.
      ...(extendedThinking ? { mode: "deep", effort } : {}),
      // Where the question was asked from, read here so it is the page as it
      // is at this moment. Removing the chip drops it.
      ...(() => {
        if (contextDismissed || !screenContextRef.current) return {};
        const ctx = readScreenContext(screenContextRef.current);
        return ctx && hasUsefulContext(ctx) ? { screenContext: ctx } : {};
      })(),
    });
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, 44), 200);
    el.style.height = `${next}px`;
  }, [input]);

  const messageText = (msg) => msg.content ?? msg.text ?? "";

  const canSend = Boolean(teamId && !sending && (input.trim() || attachments.length > 0));

  const boardFilterQ = (atMention ? atMention.query : pickerSearch).trim().toLowerCase();
  const filteredPickerBoards = useMemo(() => {
    return pickerBoards.filter((b) => {
      if (!boardFilterQ) return true;
      return String(b.name || "")
        .toLowerCase()
        .includes(boardFilterQ);
    });
  }, [pickerBoards, boardFilterQ]);

  /** All cards from loaded boards (for @ picker and Enter resolve) */
  const flatPickerCardRows = useMemo(() => {
    const rows = [];
    for (const b of pickerBoards) {
      const bid = parseInt(String(b.id), 10);
      if (Number.isNaN(bid) || bid < 1) continue;
      const st = cardsByBoardId[bid];
      const cards = st?.cards;
      if (!Array.isArray(cards)) continue;
      for (const c of cards) {
        rows.push({ boardId: bid, board: b, card: c });
      }
    }
    return rows;
  }, [pickerBoards, cardsByBoardId]);

  /** Cards filtered by same query as boards (title or board name) */
  const filteredFlatPickerCards = useMemo(() => {
    const q = boardFilterQ;
    return flatPickerCardRows.filter(({ board, card }) => {
      if (!q) return true;
      const bn = String(board.name || "").toLowerCase();
      const ct = String(card.title || "").toLowerCase();
      return bn.includes(q) || ct.includes(q);
    });
  }, [flatPickerCardRows, boardFilterQ]);

  /** Flat list: boards first, then cards — same order as `ChatAtContextPopover` keyboard nav */
  const atPickerFlatRows = useMemo(() => {
    const rows = [];
    for (const b of filteredPickerBoards) {
      const bid = parseInt(String(b.id), 10);
      if (Number.isNaN(bid) || bid < 1) continue;
      rows.push({ kind: "board", key: `board-${bid}`, board: b });
    }
    for (const { boardId, board, card } of filteredFlatPickerCards) {
      rows.push({
        kind: "card",
        key: `card-${boardId}-${card.id}`,
        boardId,
        board,
        card,
      });
    }
    return rows;
  }, [filteredPickerBoards, filteredFlatPickerCards]);

  useEffect(() => {
    setAtHighlightIndex(0);
  }, [atMention?.start, atMention?.query]);

  useEffect(() => {
    if (atPickerFlatRows.length === 0) return;
    setAtHighlightIndex((i) => Math.min(i, atPickerFlatRows.length - 1));
  }, [atPickerFlatRows.length]);

  const pickerCardsStillLoading = useMemo(() => {
    if (!pickerBoards.length) return false;
    return pickerBoards.some((b) => {
      const bid = parseInt(String(b.id), 10);
      if (Number.isNaN(bid) || bid < 1) return false;
      const st = cardsByBoardId[bid];
      return !st || st.loading;
    });
  }, [pickerBoards, cardsByBoardId]);

  /** After the first message, hide hero greeting and user/team context lines */
  const hasConversation = messages.length > 0;

  /** Landing vs ChatGPT-style docked composer (also when loading an existing thread) */
  const useDockedComposer = messages.length > 0 || messagesLoading;

  const boardAttachCount = countBoardAttachments(attachments);
  const cardAttachCount = countCardAttachments(attachments);
  const boardAddDisabled = boardAttachCount >= MAX_DASHBOARD_CONTEXT_BOARDS;
  const cardAddDisabled = cardAttachCount >= MAX_DASHBOARD_CONTEXT_CARDS;

  const syncComposerSelection = useCallback((e) => {
    const t = e.target;
    if (t.selectionStart != null) {
      setComposerSel({ start: t.selectionStart, end: t.selectionEnd });
    }
  }, []);

  const tryResolveInlineAtMention = useCallback(async () => {
    const m = atMentionRef.current;
    if (!m || !teamId) return false;
    const fq = m.query.trim().toLowerCase();
    if (!fq) return false;

    const exactBoard = pickerBoards.find((b) => (b.name || "").trim().toLowerCase() === fq);
    if (exactBoard && !boardAddDisabled) {
      addBoardAttachment(exactBoard);
      return true;
    }

    if (filteredPickerBoards.length === 1 && !boardAddDisabled) {
      addBoardAttachment(filteredPickerBoards[0]);
      return true;
    }

    const exactTitleMatches = flatPickerCardRows.filter(
      ({ card }) => (card.title || "").trim().toLowerCase() === fq
    );
    if (exactTitleMatches.length === 1 && !cardAddDisabled) {
      const r = exactTitleMatches[0];
      addCardAttachment(r.boardId, r.card);
      return true;
    }

    if (filteredFlatPickerCards.length === 1 && !cardAddDisabled) {
      const r = filteredFlatPickerCards[0];
      addCardAttachment(r.boardId, r.card);
      return true;
    }

    const boardsScope = filteredPickerBoards.length > 0 ? filteredPickerBoards : pickerBoards;
    const exactCards = [];
    const fuzzyCards = [];
    for (const b of boardsScope) {
      const bid = parseInt(String(b.id), 10);
      if (Number.isNaN(bid) || bid < 1) continue;
      const cards = await loadCardsForBoard(bid);
      if (!Array.isArray(cards)) continue;
      for (const c of cards) {
        const t = (c.title || "").trim().toLowerCase();
        if (t === fq) exactCards.push({ bid, card: c });
        else if (fq && t.includes(fq)) fuzzyCards.push({ bid, card: c });
      }
    }

    if (exactCards.length === 1 && !cardAddDisabled) {
      addCardAttachment(exactCards[0].bid, exactCards[0].card);
      return true;
    }
    if (fuzzyCards.length === 1 && !cardAddDisabled) {
      addCardAttachment(fuzzyCards[0].bid, fuzzyCards[0].card);
      return true;
    }
    return false;
  }, [
    teamId,
    pickerBoards,
    filteredPickerBoards,
    filteredFlatPickerCards,
    flatPickerCardRows,
    boardAddDisabled,
    cardAddDisabled,
    loadCardsForBoard,
    addBoardAttachment,
    addCardAttachment,
  ]);

  /** Enter sends; Shift+Enter = new line. Inline @: ↑↓ pick row, Enter confirms (or unique match). */
  const onComposerKeyDown = (e) => {
    if (e.key === "Escape") {
      if (atPickerOpen) {
        e.preventDefault();
        setAtPickerOpen(false);
        return;
      }
      if (atMentionRef.current) {
        e.preventDefault();
        stripActiveAtMention();
      }
      return;
    }

    if (atMentionRef.current && atPickerFlatRows.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setAtHighlightIndex((i) => Math.min(i + 1, atPickerFlatRows.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setAtHighlightIndex((i) => Math.max(i - 1, 0));
        return;
      }
    }

    if (e.key === "Enter") {
      if (e.shiftKey) {
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        return;
      }
      if (atMentionRef.current) {
        e.preventDefault();
        const rows = atPickerFlatRows;
        if (rows.length > 0) {
          const hi = Math.min(Math.max(0, atHighlightIndex), rows.length - 1);
          const pick = rows[hi];
          if (pick.kind === "board" && !boardAddDisabled) {
            addBoardAttachment(pick.board);
            return;
          }
          if (pick.kind === "card" && !cardAddDisabled) {
            addCardAttachment(pick.boardId, pick.card);
            return;
          }
        }
        void (async () => {
          const ok = await tryResolveInlineAtMention();
          if (!ok) {
            const el = textareaRef.current;
            if (!el) return;
            const pos = el.selectionStart ?? 0;
            setInput((prev) => prev.slice(0, pos) + "\n" + prev.slice(pos));
            requestAnimationFrame(() => {
              el.setSelectionRange(pos + 1, pos + 1);
              setComposerSel({ start: pos + 1, end: pos + 1 });
            });
          }
        })();
        return;
      }
      e.preventDefault();
      sendPrompt();
    }
  };

  const messageList = (
    <>
      {messagesHasMore && (
        <div className="flex justify-center mb-3">
          <button
            type="button"
            onClick={() => loadOlderMessages()}
            disabled={messagesLoading}
            className="text-xs text-[color:var(--trodo-primary)] hover:underline disabled:opacity-50"
          >
            {messagesLoading ? "Loading…" : "Load older messages"}
          </button>
        </div>
      )}
      <ul className="flex flex-col gap-5 sm:gap-6 pr-1">
        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const failed = msg.failed;
          const dashTags = dashboardTagsFromMessage(msg);
          const rowKey = msg._clientKey != null ? String(msg._clientKey) : String(msg.id);
          return (
            <li key={rowKey} className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
              <div
                className={
                  isUser
                    ? `max-w-[min(90%,32rem)] rounded-2xl rounded-br-md px-4 py-3 text-sm border shadow-sm ${
                        failed ? "ring-2 ring-red-300" : ""
                      }`
                    : `w-full min-w-0 max-w-[min(100%,44rem)] text-[15px] sm:text-base leading-7 ${
                        failed ? "ring-2 ring-red-300 rounded-xl p-3" : ""
                      }`
                }
                style={
                  isUser
                    ? {
                        backgroundColor: CHAT_INPUT_BG,
                        borderColor: BORDER,
                        color: theme.textPrimary,
                      }
                    : {
                        color: theme.textPrimary,
                      }
                }
              >
                {isUser && dashTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2 justify-end" aria-label="Dashboard context">
                    {dashTags.map((tag, i) => (
                      <span
                        key={`${tag.kind}-${tag.boardId}-${tag.cardId ?? "b"}-${i}`}
                        className="inline-flex max-w-[220px] truncate rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          borderColor: theme.border,
                          backgroundColor: "var(--surface-raised)",
                          color: theme.textSecondary,
                        }}
                        title={tag.label}
                      >
                        {tag.label || (tag.kind === "board" ? "Board" : "Card")}
                      </span>
                    ))}
                  </div>
                )}
                {isUser ? (
                  <div className="whitespace-pre-wrap break-words text-left">{messageText(msg)}</div>
                ) : msg.investigateStream ? (
                  (() => {
                    const inv = msg.investigateStream;
                    const rk = inv.resultKind;
                    return (
                      <div className="space-y-3 text-left w-full min-w-0">
                        {/* Trail is the single in-flight + post-hoc surface.
                            Replaces the old separate ThinkingCarousel: one
                            visual state at a time. */}
                        <InvestigationTrail inv={stopping && inv.streaming ? { ...inv, stopping: true } : inv} />

                        {rk === "clarify" && (
                          <div className="space-y-2.5">
                            <div className="whitespace-pre-wrap break-words">
                              {inv.body || "Could you clarify?"}
                            </div>
                            {/* Legacy chips, only for a question no ClarifyCard
                                owns. They answer by calling sendMessage(opt) —
                                an ordinary message, which opens a second run
                                and spends a second credit. Whenever the
                                backend recorded a clarificationId the card
                                handles it properly, so the chips would just be
                                a more expensive way to answer the same
                                question. They stay for older messages
                                persisted before clarificationId existed. */}
                            {!inv.clarificationId
                              && Array.isArray(inv.clarifyOptions) && inv.clarifyOptions.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {inv.clarifyOptions.map((opt, i) => (
                                  <button
                                    key={`${i}-${opt}`}
                                    type="button"
                                    disabled={sending}
                                    onClick={() => sendMessage(opt)}
                                    className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors text-left disabled:opacity-50"
                                    style={{
                                      borderColor: i === 0 ? "rgb(var(--trodo-primary-rgb))" : "var(--trodo-border, rgba(148,163,184,.35))",
                                      color: i === 0 ? "rgb(var(--trodo-primary-rgb))" : "inherit",
                                      backgroundColor: "transparent",
                                    }}
                                  >
                                    {opt}
                                    {i === 0 && (
                                      <span className="ml-1.5 opacity-60 font-normal">recommended</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {rk === "answer" && (
                          <>
                            {inv.answer ? (
                              <AnswerRenderer answer={inv.answer} messageId={inv.streaming ? null : msg.id} reveal={inv.revealAt || msg.revealAt || false} />
                            ) : (
                              <AnswerMarkdown
                                text={
                                  (inv.body && String(inv.body).trim()) ||
                                  (inv.streaming
                                    ? ""
                                    : "No answer text was returned. Check that the planner service is running and synthesis succeeded.")
                                }
                              />
                            )}
                            {/* Teach: 👎 records feedback on the agent run AND
                                distils a lesson recalled on future questions.
                                Only on settled, persisted answers: a streaming
                                row has no server id to attach feedback to. */}
                            {/* A deep run's progress turn. The research runs on a
                                worker and outlives this page; this follows its event
                                log: live now, replayed when the thread is reopened —
                                and reloads the thread when the answer lands. */}
                            {!inv.streaming && (msg.metadata?.ask_deep_progress || msg.metadata?.ask_plan_gate) && msg.metadata?.ask_run_id && (
                              <DeepRunProgress
                                runId={msg.metadata.ask_run_id}
                                teamId={teamId}
                                onFinished={() => reloadUntilDeepAnswer(msg.metadata.ask_run_id)}
                              />
                            )}
                            {!inv.streaming && activeSessionId && msg.id && !String(msg.id).startsWith("inv_stream_")
                              && !msg.metadata?.ask_deep_progress && !msg.metadata?.ask_plan_gate && (
                              <ChatAnswerFeedback
                                sessionId={activeSessionId}
                                messageId={msg.id}
                                teamId={teamId}
                                initial={msg.metadata?.feedback}
                              />
                            )}
                          </>
                        )}
                        {(rk === "error" || rk === "ended") && (
                          <RunEndedNote reason={inv.endReason || "failed"} text={inv.body} billingHref={billingHref} />
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <>
                    {msg.metadata?.answer && typeof msg.metadata.answer === "object" ? (
                      <AnswerRenderer answer={msg.metadata.answer} messageId={msg.id} reveal={msg.revealAt || false} />
                    ) : (
                      <AnswerMarkdown text={messageText(msg)} />
                    )}
                    {(msg.metadata?.ask_deep_progress || msg.metadata?.ask_plan_gate) && msg.metadata?.ask_run_id && (
                      <DeepRunProgress
                        runId={msg.metadata.ask_run_id}
                        teamId={teamId}
                        onFinished={() => reloadUntilDeepAnswer(msg.metadata.ask_run_id)}
                      />
                    )}
                  </>
                )}
                {msg.pending && <span className="block text-[10px] opacity-80 mt-1">Sending…</span>}
                {failed && <span className="block text-[10px] text-danger mt-1">Failed to send</span>}
              </div>
            </li>
          );
        })}
        {awaitingReply && (
          <li className="flex w-full justify-start">
            <div className="w-full min-w-0 max-w-[min(100%,44rem)] text-[15px] sm:text-base leading-7">
              <InvestigationTrail inv={RESUMING_STREAM} />
            </div>
          </li>
        )}
        <li aria-hidden />
      </ul>
    </>
  );

  // Listen for sidebar history icon click
  useEffect(() => {
    const onOpenHistory = () => {
      setAtPickerOpen(false);
      setHistoryOpen(true);
    };
    window.addEventListener("trodo:chat:openHistory", onOpenHistory);
    return () => window.removeEventListener("trodo:chat:openHistory", onOpenHistory);
  }, []);

  return (
    <div className="flex flex-1 flex-col min-h-0 h-full w-full bg-surface overflow-hidden relative">
      {useDockedComposer ? (
        <>
          <div
            ref={messagesScrollRef}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full"
          >
            <div className={`w-full mx-auto ${isPanel ? 'px-3 py-4' : 'max-w-3xl lg:max-w-4xl px-4 sm:px-6 py-6 sm:py-8'}`}>{messageList}</div>
          </div>
          <div className="shrink-0 w-full bg-surface sticky bottom-0 z-10">
            <div className={`w-full mx-auto ${isPanel ? 'px-3 pt-2 pb-3' : 'max-w-3xl lg:max-w-4xl px-4 sm:px-6 pt-3 pb-4 sm:pb-6'}`}>
              {error && teamId && (
                <p
                  className="text-xs text-danger bg-danger-soft border border-danger rounded-lg px-3 py-2 max-w-md mx-auto mb-3 w-full"
                  style={{ fontFamily: "Inter, sans-serif" }}
                  role="alert"
                >
                  {error}
                </p>
              )}
              {openClarification && (
                <ClarifyCard
                  questions={openClarification.questions}
                  onSubmit={submitClarification}
                  onSkip={skipClarification}
                  onDismiss={dismissClarification}
                  submitting={sending}
                  error={clarifyError}
                />
              )}
              <div
                ref={composerShellRef}
                className="chat-composer w-full rounded-2xl border overflow-visible transition-shadow"
                style={{ borderColor: BORDER, backgroundColor: CHAT_INPUT_BG }}
              >
                {/* Says only that Ask can see the screen. Listing the page,
                    range and filters here was describing back to someone what
                    they already have in front of them. */}
                {showContextChip && (
                  <div className="flex items-center gap-1 px-3 sm:px-4 pt-2 pb-1 text-[11px]" style={{ color: theme.textMuted }}>
                    <span>Aware of your screen</span>
                    <button
                      type="button"
                      onClick={() => setContextDismissed(true)}
                      className="rounded-full p-0.5 opacity-60 hover:opacity-100"
                      style={{ color: theme.textMuted }}
                      title="Lucid without what's on screen"
                      aria-label="Lucid without what's on screen"
                    >
                      <X size={11} strokeWidth={2} />
                    </button>
                  </div>
                )}
                {/* attachments */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-3 sm:px-4 pt-2.5 pb-1.5 border-b" style={{ borderColor: BORDER }}>
                    {attachments.map((a) => (
                      <span key={a.key} className="inline-flex items-center gap-1 max-w-full rounded-full border pl-2.5 pr-1 py-0.5 text-xs" style={{ borderColor: BORDER, backgroundColor: "var(--surface-raised)", color: theme.textPrimary }}>
                        <span className="truncate max-w-[200px] sm:max-w-[280px]">{a.label}</span>
                        <button type="button" onClick={() => removeAttachment(a.key)} className="shrink-0 rounded-full p-0.5 opacity-70 hover:opacity-100" style={{ color: theme.textSecondary }} aria-label="Remove attachment"><X size={14} strokeWidth={2} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {/* row 1: textarea */}
                <div className="relative px-4 pt-3.5 pb-1.5">
                  {showInlineAtPanel && (
                    <ChatAtContextPopover anchor="textarea" placement="above" filterQuery={atMention?.query ?? ""} showSearchField={false} pickerSearch={pickerSearch} onPickerSearchChange={setPickerSearch} pickerBoardsLoading={pickerBoardsLoading} filteredPickerBoards={filteredPickerBoards} filteredFlatPickerCards={filteredFlatPickerCards} pickerCardsStillLoading={pickerCardsStillLoading} boardAddDisabled={boardAddDisabled} cardAddDisabled={cardAddDisabled} attachments={attachments} onAddBoard={addBoardAttachment} onAddCard={addCardAttachment} flatRows={atPickerFlatRows} highlightIndex={atHighlightIndex} />
                  )}
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setError(""); syncComposerSelection(e); }}
                    onSelect={syncComposerSelection}
                    onKeyUp={syncComposerSelection}
                    onKeyDown={onComposerKeyDown}
                    placeholder="Ask Lucid about your analytics…"
                    rows={1}
                    className="w-full min-h-[40px] max-h-[200px] resize-none bg-transparent border-0 shadow-none text-[15px] sm:text-base py-0 placeholder:text-[color:var(--text-disabled)] outline-none ring-0 focus:outline-none focus:ring-0 [-webkit-tap-highlight-color:transparent]"
                    style={{ caretColor: ACCENT, color: theme.textPrimary }}
                    aria-label="Message"
                    disabled={!teamId}
                  />
                </div>
                {/* row 2: action bar */}
                <div className="flex items-center justify-between px-3 pb-3 pt-1.5">
                  {/* The + context picker is hidden for now: board/card
                      attachments had little use in chat; typing @ still works. */}
                  <ComposerModeControl
                    extended={extendedThinking}
                    onExtendedChange={setExtendedThinking}
                    effort={effort}
                    onEffortChange={setEffort}
                    disabled={!teamId}
                    placement="above"
                  />
                  {/* mic + send */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.25)] ${canDictate ? "" : "cursor-not-allowed"}`}
                      style={isListening ? { borderColor: "#fdba74", color: "#c2410c", backgroundColor: "#fff7ed" } : { borderColor: BORDER, backgroundColor: "var(--surface-raised)", color: canDictate ? theme.textPrimary : theme.textMuted }}
                      aria-label={isListening ? "Stop dictation" : "Dictate prompt"} aria-pressed={isListening} disabled={!canDictate}
                      title={canDictate ? "Speak to transcribe" : "Dictation not supported in this browser"}
                      onClick={toggleMic}
                    >
                      <Mic size={16} strokeWidth={2} className={isListening ? "animate-pulse" : ""} />
                    </button>
                    <button
                      type="button"
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-onBrand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--trodo-primary)] disabled:opacity-35 disabled:cursor-not-allowed"
                      style={{ backgroundColor: ACCENT }}
                      onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = ACCENT_HOVER; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ACCENT; }}
                      aria-label={liveRunId ? "Stop" : "Send message"} title={liveRunId ? "Stop" : undefined}
                      disabled={liveRunId ? stopping : !canSend} onClick={liveRunId ? stopRun : sendPrompt}
                    >
                      {liveRunId ? <Square size={12} strokeWidth={0} fill="currentColor" /> : <ArrowUp size={16} strokeWidth={2.5} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          ref={messagesScrollRef}
          className={`relative flex flex-1 flex-col min-h-0 overflow-y-auto w-full ${isPanel ? 'justify-end px-3 pb-3 pt-5' : hasConversation ? 'justify-center px-4 sm:px-6 py-10 sm:py-14' : 'justify-end px-4 sm:px-6 pt-10 pb-[9vh]'}`}
        >
          <JarvisBackdrop />
          <div className={`relative z-10 w-full mx-auto flex flex-col items-start text-left ${isPanel ? '' : 'max-w-3xl lg:max-w-4xl'}`}>
            {/* In the panel the composer sits at the bottom, like a chat, and
                the headline runs smaller above it. */}
            {!hasConversation && (
              <JarvisHero compact={isPanel} disabled={!teamId || sending || chatContextStatus !== "ok"} />
            )}

            {chatContextStatus === "loading" && (
              <p
                className="text-[11px] uppercase tracking-[0.18em] mb-3"
                style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                Loading workspace…
              </p>
            )}
            {chatContextStatus === "no_team" && (
              <p
                className="text-xs text-warning bg-warning-soft border border-warning rounded-lg px-3 py-2 mb-3 max-w-md"
                style={{ fontFamily: "Inter, sans-serif" }}
                role="status"
              >
                Select a workspace to use chat.
              </p>
            )}

            {error && teamId && (
              <p
                className={`text-xs text-danger bg-danger-soft border border-danger rounded-lg px-3 py-2 max-w-md ${
                  hasConversation ? "mb-3 w-full" : "mb-4"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
                role="alert"
              >
                {error}
              </p>
            )}

            <div className="jarvis-composer-wrap relative w-full">
              {/* pulsing accent line */}
              <div aria-hidden className="jarvis-composer-accent absolute left-6 right-6 -top-px h-[1px] pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgb(var(--trodo-primary-rgb) / 0.65), transparent)' }} />
              <div
                ref={composerShellRef}
                className="jarvis-composer w-full rounded-2xl border overflow-visible relative"
                style={{ borderColor: 'var(--border-default)', backgroundColor: CHAT_INPUT_BG }}
              >
                {/* Says only that Ask can see the screen. Listing the page,
                    range and filters here was describing back to someone what
                    they already have in front of them. */}
                {showContextChip && (
                  <div className="flex items-center gap-1 px-3 sm:px-4 pt-2 pb-1 text-[11px]" style={{ color: theme.textMuted }}>
                    <span>Aware of your screen</span>
                    <button
                      type="button"
                      onClick={() => setContextDismissed(true)}
                      className="rounded-full p-0.5 opacity-60 hover:opacity-100"
                      style={{ color: theme.textMuted }}
                      title="Lucid without what's on screen"
                      aria-label="Lucid without what's on screen"
                    >
                      <X size={11} strokeWidth={2} />
                    </button>
                  </div>
                )}
                {/* attachments */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-3 sm:px-4 pt-2.5 pb-1.5 border-b" style={{ borderColor: BORDER }}>
                    {attachments.map((a) => (
                      <span key={a.key} className="inline-flex items-center gap-1 max-w-full rounded-full border pl-2.5 pr-1 py-0.5 text-xs" style={{ borderColor: BORDER, backgroundColor: "var(--surface-raised)", color: theme.textPrimary }}>
                        <span className="truncate max-w-[200px] sm:max-w-[280px]">{a.label}</span>
                        <button type="button" onClick={() => removeAttachment(a.key)} className="shrink-0 rounded-full p-0.5 opacity-70 hover:opacity-100" style={{ color: theme.textSecondary }} aria-label="Remove attachment"><X size={14} strokeWidth={2} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {/* row 1: textarea */}
                <div className="relative px-4 pt-3.5 pb-1.5">
                  {showInlineAtPanel && (
                    <ChatAtContextPopover anchor="textarea" placement="above" filterQuery={atMention?.query ?? ""} showSearchField={false} pickerSearch={pickerSearch} onPickerSearchChange={setPickerSearch} pickerBoardsLoading={pickerBoardsLoading} filteredPickerBoards={filteredPickerBoards} filteredFlatPickerCards={filteredFlatPickerCards} pickerCardsStillLoading={pickerCardsStillLoading} boardAddDisabled={boardAddDisabled} cardAddDisabled={cardAddDisabled} attachments={attachments} onAddBoard={addBoardAttachment} onAddCard={addCardAttachment} flatRows={atPickerFlatRows} highlightIndex={atHighlightIndex} />
                  )}
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setError(""); syncComposerSelection(e); }}
                    onSelect={syncComposerSelection}
                    onKeyUp={syncComposerSelection}
                    onKeyDown={onComposerKeyDown}
                    placeholder="Ask Lucid about your analytics…"
                    rows={1}
                    autoFocus
                    className="w-full min-h-[40px] max-h-[200px] resize-none bg-transparent border-0 shadow-none text-[15px] sm:text-base py-0 placeholder:text-[color:var(--text-disabled)] outline-none ring-0 focus:outline-none focus:ring-0 [-webkit-tap-highlight-color:transparent]"
                    style={{ caretColor: ACCENT, color: theme.textPrimary }}
                    aria-label="Message"
                    disabled={!teamId}
                  />
                </div>
                {/* row 2: action bar */}
                <div className="flex items-center justify-between px-3 pb-3 pt-1.5">
                  {/* The + context picker is hidden for now: board/card
                      attachments had little use in chat; typing @ still works. */}
                  <ComposerModeControl
                    extended={extendedThinking}
                    onExtendedChange={setExtendedThinking}
                    effort={effort}
                    onEffortChange={setEffort}
                    disabled={!teamId}
                    placement="above"
                  />
                  {/* mic + send */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.25)] ${canDictate ? "" : "cursor-not-allowed"}`}
                      style={isListening ? { borderColor: "#fdba74", color: "#c2410c", backgroundColor: "#fff7ed" } : { borderColor: BORDER, backgroundColor: "var(--surface-raised)", color: canDictate ? theme.textPrimary : theme.textMuted }}
                      aria-label={isListening ? "Stop dictation" : "Dictate prompt"} aria-pressed={isListening} disabled={!canDictate}
                      title={canDictate ? "Speak to transcribe" : "Dictation not supported in this browser"}
                      onClick={toggleMic}
                    >
                      <Mic size={16} strokeWidth={2} className={isListening ? "animate-pulse" : ""} />
                    </button>
                    <button
                      type="button"
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-onBrand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--trodo-primary)] disabled:opacity-35 disabled:cursor-not-allowed"
                      style={{ backgroundColor: ACCENT }}
                      onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = ACCENT_HOVER; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ACCENT; }}
                      aria-label="Send message" disabled={!canSend} onClick={sendPrompt}
                    >
                      <ArrowUp size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <style>{`
            .jarvis-composer {
              box-shadow:
                0 1px 0 rgb(var(--trodo-primary-rgb) / 0.05) inset,
                0 10px 28px -14px rgb(var(--trodo-primary-rgb) / 0.16),
                0 2px 6px -2px rgb(0 0 0 / 0.06);
              transition: box-shadow .25s ease, border-color .25s ease;
            }
            .jarvis-composer:focus-within {
              border-color: rgb(var(--trodo-primary-rgb) / 0.50) !important;
              box-shadow:
                0 0 0 4px rgb(var(--trodo-primary-rgb) / 0.08),
                0 14px 36px -14px rgb(var(--trodo-primary-rgb) / 0.28),
                0 2px 8px -2px rgb(0 0 0 / 0.08);
            }
            .chat-composer {
              box-shadow: 0 2px 8px -2px rgb(0 0 0 / 0.06);
              transition: box-shadow .2s ease, border-color .2s ease;
            }
            .chat-composer:focus-within {
              border-color: rgb(var(--trodo-primary-rgb) / 0.40) !important;
              box-shadow: 0 0 0 3px rgb(var(--trodo-primary-rgb) / 0.07), 0 2px 8px -2px rgb(0 0 0 / 0.08);
            }
            .jarvis-composer-accent {
              animation: jarvisAccentPulse 2.8s ease-in-out infinite;
            }
            @keyframes jarvisAccentPulse {
              0%, 100% { opacity: .35; transform: scaleX(.86); }
              50%      { opacity: 1;   transform: scaleX(1); }
            }
            @media (prefers-reduced-motion: reduce) {
              .jarvis-composer-accent { animation: none; opacity: .6; }
            }
          `}</style>
        </div>
      )}
      {entityDrawer && (
        <ChatEntityDrawer
          open
          kind={entityDrawer.kind}
          id={entityDrawer.id}
          teamId={teamId}
          teamSlug={teamSlug}
          onClose={() => setEntityDrawer(null)}
          onOpenFullView={handleEntityOpenFullView}
        />
      )}
      {runDetailOverlay?.runId && teamId ? (
        <div className="fixed inset-0 z-[220]">
          <AgentRunDetailPage
            teamId={teamId}
            runId={runDetailOverlay.runId}
            initialSpanId={runDetailOverlay.initialSpanId}
            onClose={() => setRunDetailOverlay(null)}
          />
        </div>
      ) : null}

      {/* The conversation list, beside the conversation rather than over it —
          and beside the whole content column, so it runs the full height of
          the panel the way Ask does instead of starting under the path bar.
          The panel variant has its own history menu in its header; a 276px
          column inside a 440px panel would leave no conversation. */}
      {!isPanel && sideSlot && createPortal(
        <ChatHistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          teamId={teamId}
          activeSessionId={activeSessionId}
          onSelectSession={selectSession}
          onNewChat={newChat}
          onDeleted={(sid) => { if (activeSessionId != null && String(sid) === String(activeSessionId)) newChat(); }}
          initialSessions={sessions}
        />,
        sideSlot,
      )}
    </div>
  );
}
